import type { RuntimeRun } from "@/api/types";
import { t } from "@/i18n";

// The reasoning-depth knob is named and defaulted per provider. On Claude it
// is output_config.effort, and an empty value means adaptive thinking. On
// Gemini it is generationConfig.thinkingConfig, where an empty value means the
// block is omitted and the model's own default applies — calling that
// "adaptive" would assert a Claude behaviour about a Gemini mode. Shared by
// the Prompts, Config and «Betrieb» views so all name the knob the same way.

export interface ProviderDepth {
  // A backend predating the second provider omits it; a missing value reads
  // as "claude".
  provider?: string;
  effort: string;
}

export const isGemini = (m: ProviderDepth): boolean => m.provider === "gemini";

/** The provider's own name for the knob: Thinking on Gemini, Effort on Claude. */
export const depthLabel = (m: ProviderDepth): string =>
  isGemini(m) ? t("prompts.thinking") : t("prompts.effort");

/** The configured value, or the provider's default wording when none is set. */
export const depthValue = (m: ProviderDepth): string =>
  m.effort || (isGemini(m) ? t("prompts.modelDefault") : t("prompts.adaptive"));

/** «Model · Knob: depth», the depth sent or the model's own default. */
export function runSummary(run: RuntimeRun): string {
  const knob = { provider: run.provider, effort: run.depth };
  const depth =
    run.depth ||
    (run.default_depth ? t("ops.modelDefault", { depth: run.default_depth }) : depthValue(knob));
  return `${run.model_label} · ${depthLabel(knob)}: ${depth}`;
}
